import React from 'react'
import { createPortal } from 'react-dom'
import { Alert, Box, IconButton, Typography } from '@mui/joy'
import type { ColorPaletteProp } from '@mui/joy'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded'

export type AppBannerProps = {
  title: string
  message: React.ReactNode
  color?: ColorPaletteProp
  onDismiss?: () => void
  testId?: string
}

const iconForColor = (color: ColorPaletteProp): React.ReactElement => {
  if (color === 'danger') return <ErrorOutlineRoundedIcon />
  if (color === 'success') return <CheckCircleOutlineRoundedIcon />
  if (color === 'warning') return <WarningAmberRoundedIcon />
  return <InfoOutlinedIcon />
}

/**
 * App-level notice, portaled to document.body so it sits above layout chrome.
 * Presentational only — callers own when it shows and what it says.
 */
const AppBanner = ({
  title,
  message,
  color = 'warning',
  onDismiss,
  testId = 'app-banner',
}: AppBannerProps): React.ReactElement => {
  return createPortal(
    <Box
      data-testid={testId}
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 'var(--joy-zIndex-tooltip)',
      }}
    >
      <Alert
        color={color}
        variant="soft"
        startDecorator={iconForColor(color)}
        endDecorator={
          onDismiss != null ? (
            <IconButton
              variant="plain"
              color={color}
              size="sm"
              onClick={onDismiss}
              aria-label="Dismiss"
            >
              <CloseRoundedIcon />
            </IconButton>
          ) : null
        }
        sx={{ borderRadius: 0 }}
      >
        <Box>
          <Typography level="title-sm" fontWeight="lg">
            {title}
          </Typography>
          <Typography level="body-sm">{message}</Typography>
        </Box>
      </Alert>
    </Box>,
    document.body,
  )
}

export default AppBanner
